import React from 'react';
import { Order } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { RouteIcon } from '@hero/web-icons';
import { Collapse, RouteArrowDisplay } from '@hero/web-components';
import { OrderRouteDisplayV2 } from '../../Route/OrderRouteDisplayV2';
import { useOrderDestination } from '@hero/hooks-shared';

export interface OrderRouteDetailsProps {
  order: Order;
}

export const OrderRouteDetails: React.FC<OrderRouteDetailsProps> = ({ order }) => {
  const { t } = useTranslation('m-order');
  const { firstPickup, lastDropoff } = useOrderDestination(order.destinations);

  return (
    <div className="p-5 bg-white border border-gray-200 rounded-lg shadow-sm">
      <Collapse
        title={
          <RouteArrowDisplay
            className="text-gray-900 text-[16px] font-[600] leading-[24px]"
            origin={firstPickup}
            destinations={[lastDropoff]}
          />
        }
        icon={
          <div className="p-1.5 bg-purple-50 rounded-lg">
            <RouteIcon className="size-5 text-purple-600" />
          </div>
        }
        defaultOpen={false}>
        <div className="space-y-4">
          {/* Route Distance */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <span className="text-gray-600 text-[14px] font-[500] leading-[22px]">
              {t('drawer.distance')}
            </span>
            <span className="text-gray-900 text-[16px] font-[600] leading-[24px]">
              {order.distance.distance.text}
            </span>
          </div>

          {/*
            Route with Destinations. Customer / beneficiary chip is
            intentionally hidden inside the drawer's Details tab —
            the order drawer's header already surfaces the client name
            (via `OrderSummaryCard`'s client / "Internal" pill), so
            repeating it on every destination row was redundant noise.
            Saved-place badge + address + contact info are kept
            because they're operational info ("which warehouse?",
            "who do I call on arrival?") that lives only here.
          */}
          <OrderRouteDisplayV2
            destinations={order.destinations}
            showSavedPlace
            showAddress
            showCustomer={false}
            showContact
          />
        </div>
      </Collapse>
    </div>
  );
};
